import * as S from './styles'

import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogLabel,
} from '@reach/alert-dialog'
import { Pagination, SectionUsers, SectionUsersList } from '../../../components'
import {
  AssociatedMasterDealer,
  Pagination as PaginationType,
  UserProfile,
  RegistrationUserResponse,
  IDealerForm,
  UserActivityLog,
  Dealer,
  LogType,
  LogItemType,
  LogData,
  IUnitedGetCoopAllowanceListResponse,
} from '@monorepo/interfaces'
import {
  httpUserInfo,
  useQueryParams,
  KeycloakHelper,
  isObject,
  isNonEmptyString,
  ChannelHelper,
  MasterDealerAssociation,
  DealerHelper,
  isNonEmptyArray,
  LogHelper,
  isArrayEqual,
  isStringEqualCI,
  UserActivityLogKey,
  UserHelper,
  useDebounce,
  isNumeric,
  CoopAllowanceCapabilityType,
} from '@monorepo/infra'
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react'

import { DashboardBackButton, ExportXlsLink } from '../../atoms'
import { DealerForm, ManualAddDealerAdminForm } from '../../organisms'
import { UserForm } from './forms/userForm'
import useTextTranslation from '@monorepo/infra/build/locales/i18next/useLocation'
import axios from 'axios'
import * as TreeHelper from '../../helpers/user'
import {
  createAssociationRemoval,
  mergeScopedMasterDealerIds,
  withAllowanceSync,
} from '../../helpers/directoryRows'
import * as FileSaver from 'file-saver'
import XLSX from 'sheetjs-style'
import { useKeycloak } from '@react-keycloak/web'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import {
  SearchTerm,
  ITeamMembersListFilter,
  TeamMembersListFilter,
  filterToURLSearchParams,
} from '@monorepo/components'

type UsersFetch = PaginationType & {
  data: UserProfile[]
}

const directtvAccounts = [
  DealerHelper.masterDealerIdDirectvEmployeesOption,
  DealerHelper.masterDealerIdDirectvISPDealersOption,
  DealerHelper.masterDealerIdDirectvNDDealersOption,
]

// Manual Add Dealer Admin is restricted to these specific accounts (per PM),
// not all Super Admins.
const MANUAL_ADD_ALLOWED_EMAILS = [
  'amanda@wearetheindustrious.com',
  'george@wearetheindustrious.com',
  'jimmy@wearetheindustrious.com',
]

const getPageNumber = (pageNumberStr?: string): number => {
  const pageNumber = isNumeric(pageNumberStr)
    ? parseInt(pageNumberStr as string)
    : 1
  return pageNumber > 0 ? pageNumber : 1
}

const Users = (): JSX.Element => {
  const directoryBaseUrl = '/user/directory'

  const { keycloak } = useKeycloak()
  const user = KeycloakHelper.getTokenParsed(keycloak)
  const canAddUser =
    UserHelper.isImpersonatedUserSuperAdmin(user?.groups) ||
    UserHelper.isSuperAdmin(user?.groups) ||
    UserHelper.isImpersonatedUserDirectvAdmin(user?.groups) ||
    UserHelper.isDirectvAdmin(user?.groups)
  // Manual Add Dealer Admin is restricted to specific accounts (PM), not all Super Admins.
  const canManualAdd = MANUAL_ADD_ALLOWED_EMAILS.includes(
    (KeycloakHelper.getCurrentUserEmail(keycloak) || '').trim().toLowerCase()
  )

  const searchURLKey = 's'
  const pageURLKey = 'p'
  const queryParams = useQueryParams()
  const [search, setSearch] = useState(queryParams.get(searchURLKey) ?? '')
  const debouncedSearch = useDebounce(search, 1000)
  const [page, setPage] = useState(
    getPageNumber(queryParams.get(pageURLKey) as string)
  )

  const [isManageDialogOpened, setIsManageDialogOpened] = useState(false)
  const manageCancelRef = useRef(null)

  const { t } = useTextTranslation('admin')

  const [users, setUsers] = useState<UsersFetch>()
  const [userToEdit, setUserToEdit] = useState<IDealerForm>()
  // The master dealer whose directory row opened the form, when it was
  // opened from one. Editing is then scoped to that account.
  const [editingMasterDealerId, setEditingMasterDealerId] = useState<
    string | undefined
  >()
  const [loading, setLoading] = useState(true)
  const [removingAssociation, setRemovingAssociation] = useState(false)
  const removalInFlight = useRef(false)

  const previousApiUrl = useRef<string | undefined>(undefined)

  const [isCreateUserDialogOpened, setIsCreateUserDialogOpened] =
    useState(false)
  const [createUserError, setCreateUserError] = useState<
    string | null | undefined
  >(undefined)
  const [isManualAddDialogOpened, setIsManualAddDialogOpened] = useState(false)
  const [manualAddError, setManualAddError] = useState<
    string | null | undefined
  >(undefined)
  const [currentUserProfile, setCurrentUserProfile] = useState<
    RegistrationUserResponse | undefined
  >(undefined)
  const [isCurrentUserDataLoading, setIsCurrentUserDataLoading] =
    useState(false)
  const [principalEmails, setPrincipalEmails] = useState<
    string[] | null | undefined
  >(undefined)
  const [filter, setFilter] = useState<
    ITeamMembersListFilter | null | undefined
  >(undefined)

  useEffect(() => {
    const getPrincipalEmails = async () => {
      try {
        const response = await axios.get('/United/DealerUniqueEmails', {
          baseURL: process.env.REACT_APP_TX_BASE_URL,
          headers: {
            Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
          },
        })

        setPrincipalEmails(
          response.status === 200 && Array.isArray(response.data?.DealerEmails)
            ? response.data?.DealerEmails
            : undefined
        )
      } catch (error) {
        console.log(error)
      }
    }

    getPrincipalEmails()
  }, [])

  useEffect(() => {
    const currentUserEmail = KeycloakHelper.getCurrentUserEmail(keycloak)
    if (!currentUserEmail) {
      return
    }

    setIsCurrentUserDataLoading(true)

    httpUserInfo
      .get({ url: `/dealer/${currentUserEmail}` })
      .then((profile) => {
        const userProfile = profile.data as RegistrationUserResponse
        setCurrentUserProfile(isObject(userProfile) ? userProfile : undefined)
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setIsCurrentUserDataLoading(false)
      })
  }, [keycloak])

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const handleTeamMemberListFilterChange = (
    filter?: ITeamMembersListFilter
  ) => {
    setPage(1)
    setFilter(filter)
  }

  const getUsers = async (isReload?: boolean) => {
    const searchParams = new URLSearchParams()
    searchParams.append('page', page.toString())
    searchParams.append('pageSize', '20')
    searchParams.append('withDealers', 'true')
    searchParams.append('withCoopAllowances', 'true')

    if (isNonEmptyString(debouncedSearch) && debouncedSearch.length > 2) {
      searchParams.append('search', encodeURIComponent(debouncedSearch))
    }

    filterToURLSearchParams(searchParams, filter as ITeamMembersListFilter)

    const apiURL = `${directoryBaseUrl}?${searchParams.toString()}`

    if (!isReload && previousApiUrl.current === apiURL) {
      return
    }

    previousApiUrl.current = apiURL
    setLoading(true)
    const usersList = await httpUserInfo.get<UsersFetch>({
      url: apiURL,
    })

    setUsers(usersList.data)
    setLoading(false)
  }

  useEffect(() => {
    getUsers()
  }, [debouncedSearch, filter, page])

  const toggleModal = useCallback(() => {
    setIsManageDialogOpened((isOpened) => {
      // Closing drops the account scope, so the next edit opened from the
      // person rather than a row is not still locked to the last row's
      // master dealer.
      if (isOpened) {
        setEditingMasterDealerId(undefined)
      }
      return !isOpened
    })
  }, [])

  const handleOpenModalEditUser = async (
    index: number,
    masterDealerId?: string
  ) => {
    const data = users?.data[index] as UserProfile

    // Load the user's CURRENT groups from Keycloak (the source of truth) so the
    // role field reflects reality. Only fall back to the Mongo mirror's groups
    // if the lookup fails or returns nothing — never override with an empty
    // set — so saving can't accidentally strip groups the user actually has.
    let groups = data?.groups
    if (data?.email) {
      try {
        const response = await httpUserInfo.get<string[]>({
          url: `/user/keycloak-groups/${data.email}`,
        })
        if (Array.isArray(response?.data) && response.data.length) {
          groups = response.data
        }
      } catch (err) {
        console.error(err)
      }
    }

    const userForm: IDealerForm = {
      userId: data?._id as string,
      uniqueDealerId: data?.dealer?.uniqueDealerId,
      businessName: data?.dealer?.businessName,
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.email,
      dealerTypes: data?.dealerTypes || [],
      enabled: data?.enabled || false,
      associatedMasterDealers:
        ChannelHelper.getMasterDealerIds(
          data?.dealer?.associatedMasterDealers as MasterDealerAssociation[]
        ) || [],
      groups,
      hasCoop: data?.dealer?.hasCoop,
      channels: ChannelHelper.getAssociatedMasterDealerChannels(
        data?.dealer?.associatedMasterDealers as MasterDealerAssociation[]
      ),
      coopAllowances: data?.dealer?.coopAllowances,
    }
    setEditingMasterDealerId(masterDealerId)
    setUserToEdit(userForm)
    setIsManageDialogOpened(true)
  }

  // const { data, ...pagination } = users
  // pagination.page = Number(1)
  const handleClearSearch = () => setSearch('')

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!isNonEmptyString(userId) || !isNonEmptyString(email)) {
      return
    }

    const result = window.confirm('Are you sure?')
    if (!result) {
      return
    }

    try {
      await httpUserInfo.delete({ url: `/dealer/byuser/${email}` })
      await httpUserInfo.delete({ url: `/user/${email}` })

      LogHelper.logAsync(
        {
          userActivity: {
            userId,
            changes: [
              {
                key: UserActivityLogKey.TEAM_MEMBER_DELETED,
                previusValue: 'undefined',
                currentValue: email,
              },
            ],
          },
          type: LogType.USER_ACTIVITY,
          itemType: LogItemType.DEALER_DELETING,
        } as LogData,
        KeycloakHelper.getToken(keycloak)
      )

      getUsers(true)
    } catch (err) {
      console.error(err)
    }
  }

  const handleRemoveAssociation = async (
    user: UserProfile,
    masterDealerId: string
  ) => {
    if (removalInFlight.current || !user.email || !user.dealer?._id) return
    const confirmed = window.confirm(
      `Remove ${user.email} from master dealer ${masterDealerId}? ` +
        'Their other accounts will be kept.'
    )
    if (!confirmed) {
      return
    }

    removalInFlight.current = true
    setRemovingAssociation(true)
    let allowanceSyncSkipped = false
    try {
      // Re-read the complete directory record before replacing
      // associations, so changes made since the page loaded are preserved.
      const params = new URLSearchParams({
        page: '1',
        pageSize: '20',
        withDealers: 'true',
        withCoopAllowances: 'false',
        email: user.email,
      })
      const response = await httpUserInfo.get<UsersFetch>({
        url: `${directoryBaseUrl}?${params.toString()}`,
      })
      const current = response.data?.data?.find(
        (item) => item._id === user._id && item.dealer?._id === user.dealer?._id
      )
      if (!current?.dealer) {
        throw new Error('The current dealer record could not be loaded.')
      }
      const removal = createAssociationRemoval(
        current,
        masterDealerId,
        principalEmails || undefined
      )
      if (!removal) {
        window.alert(
          'This association cannot be removed. Refresh the directory and check its current principal status.'
        )
        return
      }

      // Sync the Co-Op allowances when they can be read, so the removed
      // account stops carrying spend. When they cannot, go ahead without
      // them: the backend only touches allowances if the body carries a
      // userEmail, so an association-only payload leaves them exactly as they
      // are. Guessing at a partial list would delete allowances on the
      // accounts the dealer keeps, and refusing outright would block the
      // removal on a third-party service being up.
      let payload = removal

      try {
        const allowances = await axios.post<
          IUnitedGetCoopAllowanceListResponse & { ErrorOccured?: boolean }
        >(
          '/United/GetCoopAllowanceList',
          { DealerEmails: [{ EmailAddress: current.email }] },
          {
            baseURL: process.env.REACT_APP_TX_BASE_URL,
            headers: {
              Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
            },
          }
        )

        if (
          allowances.status !== 200 ||
          allowances.data?.ErrorOccured ||
          !Array.isArray(allowances.data?.DealerCoopAllowance)
        ) {
          throw new Error('The Co-Op allowance list came back unusable.')
        }

        const currentAllowances = allowances.data.DealerCoopAllowance.filter(
          (item) =>
            item.EmailAddress?.trim().toLowerCase() ===
            current.email?.trim().toLowerCase()
        ).map((item) => ({
          masterDealerId: item.MasterDealerId,
          allowance: item.Allowance,
          capability: item.IsRecurring
            ? CoopAllowanceCapabilityType.MONTHLY_RECURRING
            : CoopAllowanceCapabilityType.SINGLE_ALLOCATION,
          recurringAllowance: item.RecurringAllowanceAmount,
          ...(item.Deleted && { delete: true }),
        }))

        payload = withAllowanceSync(
          removal,
          current,
          masterDealerId,
          currentAllowances
        )
      } catch (allowanceError) {
        console.error(allowanceError)
        allowanceSyncSkipped = true
      }

      await httpUserInfo.patch({
        url: `/dealer/${current.dealer._id}`,
        data: payload,
      })

      LogHelper.logAsync(
        {
          userActivity: {
            userId: user?._id,
            changes: [
              {
                key: UserActivityLogKey.TEAM_MEMBER_DELETED,
                previusValue: masterDealerId,
                currentValue: payload.associatedMasterDealers
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((a: any) => a?.masterDealerId)
                  .join(', '),
              },
            ],
          },
          type: LogType.USER_ACTIVITY,
          itemType: LogItemType.DEALER_DATA_CHANGING,
        } as LogData,
        KeycloakHelper.getToken(keycloak)
      )

      await getUsers(true)

      if (allowanceSyncSkipped) {
        window.alert(
          `${user.email} has been removed from master dealer ${masterDealerId}. ` +
            'Their Co-Op allowances could not be reached, so nothing was ' +
            'changed there - any allowance left on that account is unusable ' +
            'now that the account is gone, but ask ACB to clear it if you ' +
            'want it tidied up.'
        )
      }
    } catch (err) {
      console.error(err)
      window.alert(
        'Could not finish removing the association. Refresh the directory before trying again.'
      )
    } finally {
      removalInFlight.current = false
      setRemovingAssociation(false)
    }
  }

  const handleUserActivity = async (
    data: IDealerForm,
    userId?: string,
    changes?: UserActivityLog[]
  ) => {
    const previousData = users?.data.find((u) => u._id === data.userId)
    const currentChanges = DealerHelper.getChanges(
      previousData as unknown as Dealer,
      data,
      userId,
      changes
    )

    if (isNonEmptyArray(currentChanges.changes)) {
      LogHelper.logAsync(
        {
          userActivity: currentChanges,
          type: LogType.USER_ACTIVITY,
          itemType: LogItemType.DEALER_DATA_CHANGING,
        } as LogData,
        KeycloakHelper.getToken(keycloak)
      )
    }
  }

  const handleEditUser = async (data: IDealerForm) => {
    try {
      const userData = {
        userId: data.userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        dealerTypes: data.dealerTypes,
        enabled: data.enabled,
        groups: data.groups,
      }

      handleUserActivity(data)

      await httpUserInfo.patch({
        url: `/user/${data.userId}`,
        data: userData,
      })

      const dealer = cloneDeep(
        users?.data.find((u) => u._id === data.userId)?.dealer
      )
      if (dealer) {
        DealerHelper.patchAssociatedMasterDealers(
          dealer.associatedMasterDealers
        )

        // The association list is sent whole and replaces what is stored, so an
        // account dropped in this form would be detached with none of the
        // checks the directory rows carry - the principal account included.
        // Opened from a row, additions go through and removals are refused
        // with a pointer to the row's own button.
        let intendedIds = data.associatedMasterDealers
        if (editingMasterDealerId) {
          const change = mergeScopedMasterDealerIds(
            ChannelHelper.getMasterDealerIds(
              dealer.associatedMasterDealers as MasterDealerAssociation[]
            ),
            data.associatedMasterDealers
          )

          if (change.removalAttempted.length) {
            window.alert(
              `Accounts cannot be removed from this form. ` +
                `${change.removalAttempted.join(', ')} has been kept. ` +
                `To remove an account, use the remove button on its row in the directory.`
            )
          }

          intendedIds = change.masterDealerIds
        }

        if (
          !isArrayEqual(
            dealer.associatedMasterDealers,
            intendedIds,
            (left: AssociatedMasterDealer, right: string) =>
              isStringEqualCI(left.masterDealerId, right)
          )
        ) {
          const associatedMasterDealers =
            DealerHelper.intersectAssociatedMasterDealers(
              dealer.associatedMasterDealers,
              intendedIds
            )

          await httpUserInfo.patch({
            url: `/dealer/${dealer._id}`,
            data: {
              _id: dealer._id,
              userId: dealer.userId || data.userId,
              associatedMasterDealers,
              hasCoop: dealer.hasCoop,
              ...(!dealer._id && { userEmail: data.email }),
            },
          })
        }
      }

      setIsManageDialogOpened(false)
      setEditingMasterDealerId(undefined)
      getUsers(true)
    } catch (err) {
      console.error(err)
    }
  }

  const handleExportXls = async () => {
    try {
      const responses = await Promise.all([
        axios.get('/masterdealer/tree', {
          baseURL: process.env.REACT_APP_REST_USER_BASE_URL,
          headers: {
            Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
          },
        }),
        axios.get('/United/DealerUniqueEmails', {
          baseURL: process.env.REACT_APP_TX_BASE_URL,
          headers: {
            Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
          },
        }),
      ])

      if (Array.isArray(responses) && responses.length > 1) {
        const data = TreeHelper.getTreeDataToExport(
          responses[0].data,
          responses[1].data?.DealerEmails
        )
        const fileType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8'
        const fileExtension = '.xlsx'
        const ws = XLSX.utils.json_to_sheet(data)
        const wb = { Sheets: { Sheet1: ws }, SheetNames: ['Sheet1'] }
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const outputData = new Blob([excelBuffer], { type: fileType })
        const dateStr = moment().format('MMDDYYYY')
        FileSaver.saveAs(outputData, `Directory${dateStr}${fileExtension}`)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const createDealer = async (data: IDealerForm) => {
    try {
      const createDealerResponse = await DealerHelper.createDealerAsync(data)
      if (isNonEmptyString(createDealerResponse)) {
        setCreateUserError(createDealerResponse)
        return
      }

      handleUserActivity(
        data,
        (createDealerResponse.data as UserProfile)._id as string,
        [
          {
            key: UserActivityLogKey.TEAM_MEMBER_ADDED,
            previusValue: 'undefined',
            currentValue: (data.email as string) || 'undefined',
          },
          {
            key: UserActivityLogKey.DEALER_ID,
            previusValue: 'undefined',
            currentValue:
              (
                createDealerResponse.data as UserProfile
              )?.dealer?._id?.toString() || 'undefined',
          },
        ]
      )

      setCreateUserError(undefined)
      setIsCreateUserDialogOpened(false)
      getUsers(true)
    } catch (e) {
      console.log(e)
    }
  }

  const manualAddDealerAdmin = async (data: IDealerForm) => {
    try {
      const response = await DealerHelper.manualAddDealerAdminAsync(data)
      // A non-empty string is an error to show (incl. any future backend
      // guard message, e.g. blocking Principal changes).
      if (isNonEmptyString(response as string)) {
        setManualAddError(response as string)
        return
      }

      setManualAddError(undefined)
      setIsManualAddDialogOpened(false)
      getUsers(true)
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <S.Main>
      <DashboardBackButton redirectURL="/admin" text="Back to Admin" />
      <SectionUsers title="User Management" />
      <S.PageActions>
        {canManualAdd ? (
          <S.ManualAddAdminButton
            label="Manual Add Admin"
            colorOption="blue"
            onClick={() => setIsManualAddDialogOpened(true)}
            disabled={isCurrentUserDataLoading}
          />
        ) : undefined}
        <S.ActionButtonsContainer>
          <ExportXlsLink onClick={handleExportXls} />
          {isManageDialogOpened && (
            <S.AlertDialogOverlay
              onDismiss={toggleModal}
              leastDestructiveRef={manageCancelRef}
            >
              <S.AlertDialog
                onDismiss={toggleModal}
                leastDestructiveRef={manageCancelRef}
              >
                <AlertDialogContent
                  style={{
                    background: '#fff',
                    padding: '0 20px',
                    margin: '1vh auto',
                  }}
                >
                  <AlertDialogLabel></AlertDialogLabel>
                  <AlertDialogDescription>
                    <UserForm
                      close={toggleModal}
                      actionForm={handleEditUser}
                      initialValues={userToEdit}
                      scopedMasterDealerId={editingMasterDealerId}
                    />
                  </AlertDialogDescription>
                </AlertDialogContent>
              </S.AlertDialog>
            </S.AlertDialogOverlay>
          )}
          {canAddUser ? (
            <S.CreateUserButton
              label={t('section_users.button_text')}
              colorOption="blue"
              onClick={() => setIsCreateUserDialogOpened(true)}
              disabled={isCurrentUserDataLoading}
            />
          ) : undefined}
        </S.ActionButtonsContainer>
      </S.PageActions>
      <S.Separator />
      <S.SearchFiltersContainer>
        <S.Search
          placeholder={t('section_users_list.input_placeholder')}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <SearchTerm searchTerm={search} onClear={handleClearSearch} />
      </S.SearchFiltersContainer>
      <TeamMembersListFilter onChange={handleTeamMemberListFilterChange} />
      <SectionUsersList
        loading={loading}
        users={users?.data as UserProfile[]}
        principalEmails={principalEmails as string[]}
        handleEditUser={handleOpenModalEditUser}
        handleDeleteUser={handleDeleteUser}
        handleRemoveAssociation={handleRemoveAssociation}
        removingAssociation={removingAssociation}
      />
      <S.PaginationContainer>
        <Pagination
          onPageChange={handlePageChange}
          totalCount={users?.totalCount as number}
          page={page}
          pageSize={users?.pageSize as number}
          totalPages={users?.totalPages as number}
        />
      </S.PaginationContainer>
      <S.CreateUserDialog
        aria-label="CreateUserDialog"
        isOpen={isCreateUserDialogOpened}
        onDismiss={() => setIsCreateUserDialogOpened(false)}
      >
        <S.CreateUserDialogContent aria-label="CreateUserDialogContent">
          <DealerForm
            currentUserProfile={currentUserProfile}
            additionalMasterDealerIdsOptions={{
              position: 'top',
              options: directtvAccounts,
            }}
            actionForm={createDealer}
            close={() => setIsCreateUserDialogOpened(false)}
            error={createUserError as string}
          />
        </S.CreateUserDialogContent>
      </S.CreateUserDialog>
      <S.CreateUserDialog
        aria-label="ManualAddAdminDialog"
        isOpen={isManualAddDialogOpened}
        onDismiss={() => setIsManualAddDialogOpened(false)}
      >
        <S.CreateUserDialogContent aria-label="ManualAddAdminDialogContent">
          <ManualAddDealerAdminForm
            actionForm={manualAddDealerAdmin}
            close={() => setIsManualAddDialogOpened(false)}
            error={manualAddError as string}
          />
        </S.CreateUserDialogContent>
      </S.CreateUserDialog>{' '}
    </S.Main>
  )
}

export default Users
